/**
 * G13 / V34: per-project changelog, "since you last visited" markers and scheduled digests.
 *
 * - The changelog and the digests are derived on the user's trusted daemon from VERIFIED events
 *   (landings the pipeline verified, build records, user-signed decisions, daemon-detected
 *   blockers and failures), never from agent claims. They travel ONLY inside encrypted
 *   `hygiene.report` revisions (`report.kind` `changelog` / `digest`).
 * - The per-user "last visited" marker and the digest settings are encrypted entities
 *   (`visit`, `setting`) synced across the user's trusted devices.
 * - The hub sees an opaque digest marker (random ref + slot code + notify) so it can send an
 *   IDs-only push; it never sees digest text, project names or counts.
 */
import { z } from 'zod';
const Ts = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const ProjectId = z.string().min(1).max(128);
/** What changed. Order is the display order inside a digest section. */
export const CHANGELOG_KINDS = [
    'release',
    'landed',
    'build',
    'decision',
    'task-done',
    'task-blocked',
    'failure',
];
export const ChangelogKind = z.enum(CHANGELOG_KINDS);
export const CHANGELOG_KIND_LABEL = {
    release: 'Released',
    landed: 'Landed',
    build: 'Built',
    decision: 'Decided',
    'task-done': 'Tasks completed',
    'task-blocked': 'Blocked',
    failure: 'Failures',
};
/** One piece of evidence an item links to (every item is drillable to at least one). */
export const EvidenceKind = z.enum([
    'commit',
    'landing',
    'build',
    'run',
    'task',
    'workstream',
    'question',
    'blocker',
    'lane',
    'link',
]);
export const EvidenceRef = z.strictObject({
    kind: EvidenceKind,
    id: z.string().min(1).max(200),
    label: z.string().max(200).optional(),
    /** Only http(s) links (PR, store page, deployment). */
    url: z
        .string()
        .max(2000)
        .regex(/^https?:\/\//)
        .optional(),
});
export const ChangelogItemId = z.string().regex(/^[A-Za-z0-9:_.-]{1,200}$/);
export const ChangelogItem = z.strictObject({
    /** Stable dedupe key (e.g. `landed:<landingId>`, `failure:build:<laneId>`). */
    id: ChangelogItemId,
    projectId: ProjectId,
    kind: ChangelogKind,
    /** Latest occurrence. */
    at: Ts,
    /** First occurrence (grouped items repeat, e.g. the same lane failing three times). */
    firstAt: Ts,
    /** Human-readable summary (verified source text: landing summary, build changelog, …). */
    title: z.string().min(1).max(300),
    detail: z.string().max(1000).optional(),
    /** Occurrences folded into this item (never truncated in the UI). */
    count: z.number().int().positive().max(1_000_000),
    evidence: z.array(EvidenceRef).min(1).max(24),
    workstreamId: z.string().max(128).optional(),
    runId: z.string().max(128).optional(),
});
export const CHANGELOG_MAX_ITEMS = 200;
/** Rolling window the daemon keeps per project. */
export const CHANGELOG_WINDOW_MS = 14 * 86_400_000;
/** `hygiene.report.report` payload: the rolling per-project changelog (newest first). */
export const ChangelogReport = z.strictObject({
    kind: z.literal('changelog'),
    projectId: ProjectId,
    projectName: z.string().max(200).optional(),
    updatedAt: Ts,
    items: z.array(ChangelogItem).max(CHANGELOG_MAX_ITEMS),
});
export const DIGEST_SLOTS = ['morning', 'afternoon', 'evening'];
export const DigestSlot = z.enum(DIGEST_SLOTS);
export const DIGEST_SLOT_LABEL = {
    morning: 'Morning',
    afternoon: 'Afternoon',
    evening: 'Evening',
};
const Counts = z.strictObject(Object.fromEntries(CHANGELOG_KINDS.map((k) => [k, z.number().int().nonnegative().optional()])));
export const DigestSection = z.strictObject({
    projectId: ProjectId,
    projectName: z.string().max(200).optional(),
    counts: Counts,
    items: z.array(ChangelogItem).max(60),
    /** Items beyond `items` (the counts stay exact). */
    more: z.number().int().nonnegative(),
});
export const DigestEmailState = z.strictObject({
    state: z.enum(['sent', 'failed', 'skipped']),
    at: Ts,
    /** Recipient count only (addresses stay in the encrypted settings). */
    recipients: z.number().int().nonnegative().optional(),
    detail: z.string().max(300).optional(),
});
/** `hygiene.report.report` payload: one scheduled digest (latest per scope + slot). */
export const DigestReport = z.strictObject({
    kind: z.literal('digest'),
    digestId: z.string().regex(/^[A-Za-z0-9:_.-]{1,200}$/),
    slot: DigestSlot,
    /** `all` = cross-project; otherwise one project id. */
    scope: z.union([z.literal('all'), z.strictObject({ projectId: ProjectId })]),
    from: Ts,
    to: Ts,
    generatedAt: Ts,
    /** Deterministic from the counts (no model text). */
    headline: z.string().min(1).max(300),
    sections: z.array(DigestSection).max(64),
    email: DigestEmailState.optional(),
});
// ---- encrypted entities ------------------------------------------------------------------------
/** Entity value for kind `visit`: when this user last opened the project (any device). */
export const LastVisitEntry = z.strictObject({
    projectId: ProjectId,
    visitedAt: Ts,
});
const Hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
const OpRef = z
    .string()
    .max(500)
    .regex(/^op:\/\/[^\s]+$/, 'must be a 1Password op:// reference');
const Email = z
    .string()
    .max(254)
    .regex(/^[^\s@<>,;"]+@[^\s@<>,;"]+\.[^\s@<>,;"]+$/);
export const DigestEmailTransport = z.discriminatedUnion('type', [
    z.strictObject({
        type: z.literal('smtp'),
        host: z
            .string()
            .min(1)
            .max(253)
            .regex(/^[A-Za-z0-9.-]+$/),
        port: z.number().int().min(1).max(65535),
        /** `tls` = implicit TLS (465); `starttls` upgrades (587). Plaintext SMTP is not offered. */
        security: z.enum(['tls', 'starttls']),
        /** Username: literal or op:// reference. */
        username: z.string().min(1).max(500),
        password: OpRef,
    }),
    z.strictObject({
        type: z.literal('api'),
        provider: z.enum(['resend', 'postmark']),
        apiKey: OpRef,
    }),
]);
export const DigestEmailSettings = z.strictObject({
    enabled: z.boolean(),
    /** Set when the user confirmed that email leaves Viko's end-to-end encryption boundary. */
    acknowledgedAt: Ts.optional(),
    from: Email,
    to: z.array(Email).min(1).max(10),
    /** Per-digest opt-in: only these slots are emailed. */
    slots: z.array(DigestSlot).max(3),
    /** Cross-project digest only (default) or also each per-project digest. */
    perProject: z.boolean().optional(),
    transport: DigestEmailTransport,
});
export const DigestSettings = z.strictObject({
    version: z.literal(1),
    /** IANA zone the times are in (the browser's zone when saved). */
    timezone: z.string().min(1).max(64),
    slots: z.strictObject({
        morning: z.strictObject({ enabled: z.boolean(), time: Hhmm }),
        afternoon: z.strictObject({ enabled: z.boolean(), time: Hhmm }),
        evening: z.strictObject({ enabled: z.boolean(), time: Hhmm }),
    }),
    crossProject: z.boolean(),
    perProject: z.boolean(),
    /** Projects excluded from digests (default: every project included). */
    excluded: z.array(ProjectId).max(500),
    /** Push (IDs-only) when a digest is ready and has something in it. */
    push: z.boolean(),
    email: DigestEmailSettings.optional(),
    updatedAt: Ts,
});
export const DEFAULT_DIGEST_SETTINGS = {
    version: 1,
    timezone: 'UTC',
    slots: {
        morning: { enabled: true, time: '08:00' },
        afternoon: { enabled: true, time: '13:00' },
        evening: { enabled: true, time: '18:00' },
    },
    crossProject: true,
    perProject: false,
    excluded: [],
    push: true,
    updatedAt: 0,
};
/** Entity value for kind `setting` (one entity per setting name). */
export const SettingEntry = z.strictObject({
    /** `digests`: DigestSettings; `providerBilling`: per-account subscription prices (G4);
     * `latency`: blocked→known timing samples (G12); `routing*`: stated/learned routing (G11). */
    name: z.enum([
        'digests',
        'providerBilling',
        'latency',
        'routingPreferences',
        'routingOutcomes',
        'routingOutcome',
        'routingCorrections',
        // ---- g5-setup: learned project-configuration tendencies (ProjectTendencies) ----
        'tendencies',
        // ---- end g5-setup ----
        // ---- bark-route: default bark routing, global and per project (BarkRouting) ----
        'barkRoute',
        'barkRouteProject',
        // ---- end bark-route ----
    ]),
    value: z.record(z.string(), z.unknown()),
    updatedAt: Ts,
});
/** Deterministic UUIDv4-shaped id so every device writes the same entity for the same key. */
export function stableEntityId(namespace, key) {
    const s = `${namespace}\u0000${key}`;
    const words = [0x811c9dc5, 0x01000193, 0x9e3779b9, 0x85ebca6b];
    for (let i = 0; i < s.length; i++) {
        const c = s.charCodeAt(i);
        for (let w = 0; w < 4; w++) {
            words[w] = Math.imul(words[w] ^ (c + w * 31), 0x01000193 + w * 2) >>> 0;
        }
    }
    const hex = words.map((w) => w.toString(16).padStart(8, '0')).join('');
    const v = ((parseInt(hex[16], 16) & 0x3) | 0x8).toString(16);
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-${v}${hex.slice(17, 20)}-${hex.slice(20, 32)}`;
}
export const lastVisitEntityId = (projectId) => stableEntityId('visit', projectId);
export const settingEntityId = (name) => stableEntityId('setting', name);
/** bark-route: one `barkRouteProject` setting per project. */
export const barkRouteProjectEntityId = (projectId) => stableEntityId('setting:barkRouteProject', projectId);
/** G11: one entity per run for `routingOutcome` / `routingCorrections` (no shared document). */
export const routingRunEntityId = (name, runId) => stableEntityId(`setting:${name}`, runId);
// ---- hub-visible marker ------------------------------------------------------------------------
/** Opaque digest-ready marker in the scheduling status (`notify` = send one IDs-only push). */
export const ScheduleDigest = z.strictObject({
    ref: z.string().regex(/^[A-Za-z0-9_-]{22}$/),
    slot: z
        .number()
        .int()
        .min(0)
        .max(DIGEST_SLOTS.length - 1),
    notify: z.boolean(),
});
// ---- shared pure helpers (daemon + browser) ----------------------------------------------------
export function countItems(items) {
    const counts = {};
    for (const i of items)
        counts[i.kind] = (counts[i.kind] ?? 0) + i.count;
    return counts;
}
/** "2 landed · 1 released · 1 blocked" — deterministic, from counts only. */
export function digestHeadline(counts) {
    const words = {
        release: ['release', 'releases'],
        landed: ['change landed', 'changes landed'],
        build: ['build', 'builds'],
        decision: ['decision', 'decisions'],
        'task-done': ['task completed', 'tasks completed'],
        'task-blocked': ['blocked', 'blocked'],
        failure: ['failure', 'failures'],
    };
    const parts = CHANGELOG_KINDS.flatMap((k) => {
        const n = counts[k] ?? 0;
        return n ? [`${n} ${words[k][n === 1 ? 0 : 1]}`] : [];
    });
    return parts.length ? parts.join(' · ') : 'Nothing verified changed';
}
/** Items newer than the marker (strictly after), newest first. No marker = everything. */
export function itemsSince(items, visitedAt) {
    return items
        .filter((i) => visitedAt === undefined || i.at > visitedAt)
        .sort((a, b) => b.at - a.at);
}
/** Cross-device merge: the newest visit wins; a stale device never moves the marker back. */
export function mergeVisit(current, next) {
    return current && current.visitedAt >= next.visitedAt ? current : next;
}
//# sourceMappingURL=digests.js.map